"use client";

import { useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import {
  FaAward,
  FaClipboardCheck,
  FaGift,
  FaSeedling,
  FaServicestack,
  FaTools,
  FaTractor,
} from "react-icons/fa";

interface VendorCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const VendorCard = ({ icon, title, description }: VendorCardProps) => (
  <div className="flex items-start gap-4 bg-white border border-gray-200 p-6 rounded-sm shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md group">
    <div className="p-3 bg-green-50 rounded-sm text-green-800 group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <div>
      <h3 className="font-bold text-gray-900 text-lg mb-1">
        {title}
      </h3>
      <p className="text-sm font-medium text-gray-600 leading-relaxed">
        {description}
      </p>
    </div>
  </div>
);

interface BulletItemProps {
  children: React.ReactNode;
}

const BulletItem = ({ children }: BulletItemProps) => (
  <li className="flex items-start gap-3">
    <span className="mt-1.5 w-2 h-2 rounded-sm bg-green-800 flex-shrink-0" />
    <span className="text-gray-800 text-sm sm:text-base font-medium leading-relaxed">{children}</span>
  </li>
);

interface GuidelineItemProps {
  number: number;
  title: string;
  description: string;
}

const GuidelineItem = ({ number, title, description }: GuidelineItemProps) => (
  <div className="flex gap-3">
    <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-sm bg-green-50 text-green-800 font-bold text-xs mt-0.5">
      {number}
    </span>
    <div>
      <h4 className="font-bold text-gray-900 text-sm sm:text-base mb-1">{title}</h4>
      <p className="text-gray-600 text-xs sm:text-sm font-medium leading-relaxed">{description}</p>
    </div>
  </div>
);

function VendorToolKitContent() {
  const searchParams = useSearchParams();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          const yOffset = -99;
          const y =
            el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    }
  }, [searchParams]);

  return (
    <div className="bg-white min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-12">

        {/* Header Block */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-sm bg-green-50 text-green-800 mb-2">
            <FaTractor className="text-3xl" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-green-900 tracking-tight">
            Vendor Tool Kit
          </h1>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto font-medium">
            Everything you need to showcase agriculture offerings, follow product and service guidelines, and access verified buyer leads on our platform.
          </p>
        </div>

        {/* Section 1: Who Can Be a Vendor */}
        <div id="WCV" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm space-y-6">
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-green-800 mb-2">
              Who Can Be a Vendor?
            </h2>
            <p className="text-gray-500 text-sm font-medium max-w-lg mx-auto">
              Eligible vendors registered under the platform include:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <VendorCard
              icon={<FaTractor className="text-2xl" />}
              title="Farmers"
              description="Sell produce directly to bulk buyers."
            />
            <VendorCard
              icon={<FaSeedling className="text-2xl" />}
              title="Input Suppliers"
              description="Supplying fertilizers, seeds, machinery, tools & more."
            />
            <VendorCard
              icon={<FaTools className="text-2xl" />}
              title="Service Providers"
              description="Offering on-farm or off-farm agri services."
            />
          </div>
        </div>

        {/* Section 2: Guidelines – Products */}
        <div id="GDPV" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm">
          <h2 className="flex items-center gap-3 text-green-800 text-xl font-bold border-b border-gray-100 pb-3 mb-6">
            <FaClipboardCheck className="text-2xl text-green-800" />
            <span>Vendor Guidelines – Products</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <GuidelineItem
              number={1}
              title="Accurate Product Listings"
              description="Include clear descriptions, quality images, accurate pricing, current availability, location, and expected delivery timelines."
            />
            <GuidelineItem
              number={2}
              title="Transparent & Competitive Pricing"
              description="Prices should reflect market conditions. Mention any extra charges like packaging or delivery upfront."
            />
            <GuidelineItem
              number={3}
              title="Timely Response to Inquiries"
              description="Respond to customer messages within 12 hours. Delays or ghosting can get your listing temporarily suspended."
            />
            <GuidelineItem
              number={4}
              title="Up-to-Date Product Catalog"
              description="Don't let buyers browse expired listings. Keep your catalog fresh and relevant to improve buyer confidence."
            />
          </div>
        </div>

        {/* Section 3: Guidelines – Services */}
        <div id="GSV" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm">
          <h2 className="flex items-center gap-3 text-green-800 text-xl font-bold border-b border-gray-100 pb-3 mb-6">
            <FaServicestack className="text-2xl text-green-800" />
            <span>Vendor Guidelines – Services</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <GuidelineItem
              number={1}
              title="Clear Service Details"
              description="Specify service types, coverage areas, charges, and expected timelines."
            />
            <GuidelineItem
              number={2}
              title="Honest and Transparent Info"
              description="Keep your service descriptions truthful. False claims are strictly prohibited."
            />
            <GuidelineItem
              number={3}
              title="Professional Interaction"
              description="Maintain respectful, professional communication to build long-term credibility."
            />
          </div>
        </div>

        {/* Section 4: Vendor Benefits */}
        <div id="HBU" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm">
          <h2 className="flex items-center gap-3 text-green-800 text-xl font-bold border-b border-gray-100 pb-3 mb-5">
            <FaGift className="text-2xl text-green-800" />
            <span>Vendor Benefits</span>
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <BulletItem>Showcase your offerings to a wide, targeted audience.</BulletItem>
            <BulletItem>Get qualified buyer leads at no extra marketing cost.</BulletItem>
            <BulletItem>Build direct brand credibility with clients.</BulletItem>
            <BulletItem>Easy direct communication via calls or WhatsApp.</BulletItem>
            <BulletItem>Affordable promotion compared to offline directories.</BulletItem>
          </ul>
        </div>

        {/* Section 5: Get Your Leads */}
        <div id="GYL" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm space-y-6">
          <h2 className="flex items-center gap-3 text-green-800 text-xl font-bold border-b border-gray-100 pb-3">
            <FaAward className="text-2xl text-green-800" />
            <span>Get Your Leads</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* How It Works */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">How It Works</h3>
              <ul className="space-y-2.5">
                <BulletItem>List offerings with name, category, and pricing.</BulletItem>
                <BulletItem>Buyers submit verified enquiries.</BulletItem>
                <BulletItem>Access full contact info and follow up directly.</BulletItem>
              </ul>
            </div>

            {/* Why It Works */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Why It Works</h3>
              <ul className="space-y-2.5">
                <BulletItem>Multi-channel promotion brings active regional traffic.</BulletItem>
                <BulletItem>Leads are filtered strictly by category matching.</BulletItem>
                <BulletItem>Regular updates maximize conversions.</BulletItem>
              </ul>
            </div>

            {/* Terms & Conditions */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Terms &amp; Conditions</h3>
              <ul className="space-y-2.5">
                <BulletItem>Strictly agriculture-related products &amp; services.</BulletItem>
                <BulletItem>Maintain professional communication.</BulletItem>
                <BulletItem>Abuse or spam leads to immediate account suspension.</BulletItem>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default function VendorToolKitPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gray-500 font-medium text-sm">Loading Vendor Tool Kit...</div>}>
      <VendorToolKitContent />
    </Suspense>
  );
}
